import Button from '@mui/material/Button';
import PropTypes from "prop-types";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Box, Divider, IconButton, Typography, TextField, Stack } from '@mui/material';
import { Iconify } from '../iconify';

function ConfirmDialogWithComment({
  title,
  content,
  action,
  open,
  icon,
  onClose,
  showCancel = true,
  showCloseButton = false,
  showDivider = false,
  confirmText,
  placeholder,
  onTextChange,
  textValue,
  onSubmit,
  confirmBtnLabel = 'Yes, Cancel',
  confirmBtnDisabled = false,
  ...other
}) {
  const renderContent = () => {
    if (content) {
      return content;
    }
    return (
      <Stack
        spacing={2}
        sx={{
          width: '100%',
          minWidth: { xs: '280px', sm: '400px' },
          maxWidth: { xs: '90vw', sm: '500px' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {confirmText && (
            <Typography variant="body2">{confirmText}</Typography>
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            value={textValue || ''}
            onChange={onTextChange}
            inputProps={{ maxLength: 500 }}
            sx={{
              my: 1, '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
            placeholder={placeholder || 'Enter your comment here'}
            helperText={`${textValue?.length}/500 characters`}

          />
        </Box>
      </Stack>
    );
  };

  const renderAction = () => {
    if (action) {
      return action;
    }

    return (
      <Button
        variant="contained"
        color="error"
        disabled={confirmBtnDisabled}
        onClick={onSubmit}
        sx={{
          fontSize: '15px',
          fontWeight: '600',
          color: '#fff',
          background: '#1A407D',
          minWidth: { xs: '120px', lg: '204px' },
          height: '48px',
          borderRadius: '8px',
          textTransform: 'none',
          margin: 0,
          '&:hover': {
            background: '#1A407D',
            boxShadow: 'none',
          },
        }}
      >
        {confirmBtnLabel}
      </Button>
    );
  };


  return (
    <Dialog
      fullWidth
      sx={{
        maxWidth: {
          lg: '558px',
        },
        margin: '0px auto',
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        },
      }}
      {...other}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '40px 24px',
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            m: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: '#1C252E',
            mb: showDivider ? 0 : 1,
            display: 'flex',
            justifyContent: showCloseButton ? 'start' : 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: showCloseButton ? 1 : 'none',
              justifyContent: showCloseButton ? 'start' : 'flex-start',
            }}
          >
            {icon?.name && <Iconify icon={icon.name} width={icon.width ?? undefined} />}
            {title}
          </Box>
          {showCloseButton && (
            <IconButton onClick={onClose} sx={{ color: 'grey.500', p: 0.5, ml: 1 }}>
              <Iconify icon="eva:close-fill" width={24} />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent
          sx={{
            typography: 'body2',
            textAlign: 'center',
            p: 0,
            m: 0,
            fontSize: '14px',
            fontWeight: '400',
            color: '#1C252E',
            mb: 3,
          }}
        >
          {showDivider && (<Divider sx={{ mt: 2, mb: 2, borderStyle: 'dashed', borderColor: '#DADADA' }} />
          )}
          {renderContent()}
        </DialogContent>

        <DialogActions
          sx={{
            m: '0',
            p: '0',
            gap: {
              xs: 1,
              lg: 3,
            },
          }}
        >
          {renderAction()}

          {showCancel && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
              sx={{
                fontSize: '15px',
                fontWeight: '600',
                color: '#1A407D',
                height: '48px',
                borderRadius: '8px',
                border: '1px solid #D0D5DD',
                textTransform: 'none',
                minWidth: {
                  xs: '120px',
                  lg: '204px',
                },
              }}
            >
              No
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}



ConfirmDialogWithComment.propTypes = {
  action: PropTypes.func,
  confirmBtnDisabled: PropTypes.any,
  confirmBtnLabel: PropTypes.any,
  confirmText: PropTypes.any,
  content: PropTypes.any,
  icon: PropTypes.any,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  onTextChange: PropTypes.func,
  open: PropTypes.any,
  placeholder: PropTypes.any,
  showCancel: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  showDivider: PropTypes.bool,
  textValue: PropTypes.any,
  title: PropTypes.any,
};

export default ConfirmDialogWithComment;